import { useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useDrop } from 'react-dnd';
import Element from '../Element';
import { Element as ElementType, FieldElement, CombinationResult } from '../../types';
import { useCombinationCheck } from '../../hooks';
import LoadingIndicator from './LoadingIndicator';
import NewElementAnimation from './NewElementAnimation';
import GameFieldHint from './GameFieldHint';
import './GameField.css';

// Константы
const ANIMATION_DURATION = 2000;
const ELEMENT_FIELD_ID_PREFIX = 'field-';

interface GameFieldProps {
  elements: ElementType[];
  onCombine: (element1: ElementType, element2: ElementType) => Promise<CombinationResult | null>;
  isLoading?: boolean;
}

// Интерфейс для императивного хендла
export interface GameFieldHandle {
  addElementToRandomPosition: (element: ElementType) => void;
}

/**
 * Компонент игрового поля
 */
const GameField = forwardRef<GameFieldHandle, GameFieldProps>((props, ref) => {
  const { elements, onCombine, isLoading = false } = props;
  
  // Состояние для хранения элементов на поле
  const [elementsOnField, setElementsOnField] = useState<FieldElement[]>([]);
  // Состояние для анимации нового элемента
  const [animation, setAnimation] = useState<{
    element: ElementType;
    position: { x: number; y: number };
    visible: boolean;
  } | null>(null);
  
  // Ref для доступа к DOM-элементу поля
  const fieldRef = useRef<HTMLDivElement>(null);

  // Хук для проверки комбинаций
  const { checkCombination } = useCombinationCheck(onCombine);

  // Функция для добавления элемента на поле
  const addElementToField = useCallback((element: ElementType, position: { x: number; y: number }) => {
    const fieldId = `${ELEMENT_FIELD_ID_PREFIX}${element._id}-${Date.now()}`;
    
    const newElement: FieldElement = {
      ...element,
      fieldId,
      position
    };
    
    setElementsOnField(prev => [...prev, newElement]);
  }, []);

  // Функция для добавления элемента в случайную позицию
  const addElementToRandomPosition = useCallback((element: ElementType) => {
    if (fieldRef.current) {
      const fieldRect = fieldRef.current.getBoundingClientRect();
      const fieldWidth = fieldRect.width;
      const fieldHeight = fieldRect.height;
      
      // Вычисляем случайную позицию в пределах поля
      // Оставляем отступ от края, чтобы элемент целиком оставался на поле
      const padding = 50; // примерный размер элемента
      const x = Math.random() * (fieldWidth - padding * 2) + padding;
      const y = Math.random() * (fieldHeight - padding * 2) + padding;
      
      addElementToField(element, { x, y });
    }
  }, [addElementToField]);

  // Экспортируем методы через ref
  useImperativeHandle(ref, () => ({
    addElementToRandomPosition
  }));

  // Функция для обновления позиции элемента на поле
  const updateElementPosition = useCallback((fieldId: string, position: { x: number; y: number }) => {
    setElementsOnField(prev => 
      prev.map(element => 
        element.fieldId === fieldId 
          ? { ...element, position } 
          : element
      )
    );
  }, []);

  // Функция для удаления элемента с поля
  const removeElementFromField = useCallback((fieldId: string) => {
    console.log(`Удаление элемента с ID: ${fieldId}`);
    setElementsOnField(prev => {
      const newElements = prev.filter(e => e.fieldId !== fieldId);
      console.log(`Элементов на поле было: ${prev.length}, стало: ${newElements.length}`);
      return newElements;
    });
  }, []);

  // Обработка успешной комбинации элементов
  const handleSuccessfulCombination = useCallback((
    result: CombinationResult, 
    centerPosition: { x: number; y: number },
    element1Id: string,
    element2Id: string
  ) => {
    // Удаляем исходные элементы с задержкой
    setTimeout(() => {
      removeElementFromField(element1Id);
      removeElementFromField(element2Id);
      console.log('Элементы удалены с поля');
    }, 0);
    
    if (result.isNew) {
      // Если это новый элемент, показываем анимацию
      console.log('Новый элемент, показываем анимацию');
      setAnimation({
        element: result.newElement,
        position: centerPosition,
        visible: true
      });
      
      // Скрываем анимацию через 2 секунды и добавляем элемент на поле
      setTimeout(() => {
        setAnimation(null);
        addElementToField(result.newElement, centerPosition);
        console.log('Анимация завершена, элемент добавлен на поле');
      }, ANIMATION_DURATION);
    } else {
      // Если это не новый элемент, просто добавляем его на поле
      console.log('Не новый элемент, добавляем на поле сразу');
      addElementToField(result.newElement, centerPosition);
    }
  }, [removeElementFromField, addElementToField]);

  // Обработчик перемещения элемента
  const handleElementDrop = useCallback(async (element: FieldElement) => {
    console.log(`Обработка перетаскивания элемента: ${element.name}, fieldId: ${element.fieldId}`);
    
    // Проверяем возможность комбинации с другими элементами на поле
    for (const otherElement of elementsOnField) {
      if (otherElement.fieldId !== element.fieldId) {
        console.log(`Проверка элемента с другим элементом: ${otherElement.name}, fieldId: ${otherElement.fieldId}`);
        
        const { 
          success, 
          result, 
          centerPosition, 
          element1Id, 
          element2Id 
        } = await checkCombination(element, otherElement);
        
        if (success && result && centerPosition && element1Id && element2Id) {
          handleSuccessfulCombination(result, centerPosition, element1Id, element2Id);
          return true;
        }
      }
    }
    
    console.log('Комбинации не найдены');
    return false;
  }, [elementsOnField, checkCombination, handleSuccessfulCombination]);

  // Настройка области для дропа с useMemo для оптимизации
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'element',
    hover: (item: any, monitor) => {
      // Если элемент уже на поле, обновляем его позицию при перемещении
      if (item.isOnField && item.fieldId && fieldRef.current) {
        const dropPosition = monitor.getClientOffset();
        
        if (dropPosition) {
          const fieldRect = fieldRef.current.getBoundingClientRect();
          const position = {
            x: dropPosition.x - fieldRect.left,
            y: dropPosition.y - fieldRect.top
          };
          
          // Обновляем позицию элемента в реальном времени
          updateElementPosition(item.fieldId, position);
        }
      }
    },
    drop: (item: any, monitor) => {
      const dropPosition = monitor.getClientOffset();
      
      if (dropPosition && fieldRef.current) {
        const fieldRect = fieldRef.current.getBoundingClientRect();
        const position = {
          x: dropPosition.x - fieldRect.left,
          y: dropPosition.y - fieldRect.top
        };
        
        // Проверяем, что элемент еще не на поле
        if (!item.isOnField) {
          console.log('Добавление нового элемента на поле или скрещивание');
          const element = elements.find(e => e._id === item._id);
          
          if (element) {
            // Проверяем, есть ли рядом с местом сброса другие элементы
            const nearbyElement = elementsOnField.find(fieldElement => {
              const distance = Math.sqrt(
                Math.pow(fieldElement.position.x - position.x, 2) +
                Math.pow(fieldElement.position.y - position.y, 2)
              );
              return distance < 100; // Та же дистанция, что и в checkCombination
            });
            
            if (nearbyElement) {
              console.log('Найден элемент рядом с местом сброса, пытаемся скрестить');
              // Создаем временный элемент на поле для скрещивания
              const tempFieldElement: FieldElement = {
                ...element,
                fieldId: `temp-${Date.now()}`,
                position
              };
              
              // Асинхронно проверяем комбинацию
              setTimeout(async () => {
                const { 
                  success, 
                  result, 
                  centerPosition, 
                  element1Id, 
                  element2Id 
                } = await checkCombination(tempFieldElement, nearbyElement);
                
                if (success && result && centerPosition && element1Id && element2Id) {
                  // Скрещивание успешно, удаляем только элемент, который был на поле
                  removeElementFromField(nearbyElement.fieldId);
                  
                  // Обрабатываем результат скрещивания
                  if (result.isNew) {
                    // Если новый элемент, показываем анимацию
                    setAnimation({
                      element: result.newElement,
                      position: centerPosition,
                      visible: true
                    });
                    
                    setTimeout(() => {
                      setAnimation(null);
                      addElementToField(result.newElement, centerPosition);
                    }, ANIMATION_DURATION);
                  } else {
                    // Если не новый, просто добавляем на поле
                    addElementToField(result.newElement, centerPosition);
                  }
                } else {
                  // Если скрещивание не удалось, просто добавляем элемент на поле
                  addElementToField(element, position);
                }
              }, 0);
            } else {
              // Если рядом нет элементов, просто добавляем на поле
              addElementToField(element, position);
            }
          }
        } else {
          console.log(`Обновление позиции существующего элемента на поле: ${item.fieldId}`);
          // Элемент уже на поле - обновляем его позицию
          updateElementPosition(item.fieldId, position);
          
          // Проверяем возможные комбинации
          const fieldElement = elementsOnField.find(e => e.fieldId === item.fieldId);
          if (fieldElement) {
            console.log('Проверка комбинаций для перемещенного элемента');
            // Обработка асинхронная, вызываем через setTimeout чтобы избежать проблем с состоянием
            setTimeout(() => {
              handleElementDrop({...fieldElement, position});
            }, 0);
          }
        }
      }
      return undefined;
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }), [elements, elementsOnField, addElementToField, updateElementPosition, handleElementDrop]);

  // Формирование классов для поля
  const fieldClassName = useMemo(() => {
    return [
      'game-field',
      isOver ? 'drag-over' : '',
      isLoading ? 'loading' : ''
    ].filter(Boolean).join(' ');
  }, [isOver, isLoading]);

  // Настройка ref для поля
  const setFieldRef = useCallback((node: HTMLDivElement | null) => {
    drop(node);
    fieldRef.current = node;
  }, [drop]);

  return (
    <div 
      ref={setFieldRef}
      className={fieldClassName}
    >
      {/* Подсказка */}
      {elementsOnField.length === 0 && <GameFieldHint />}
      
      {/* Индикатор загрузки */}
      {isLoading && <LoadingIndicator />}
      
      {/* Элементы на поле */}
      {elementsOnField.map(element => (
        <Element
          key={element.fieldId}
          element={element}
          fieldId={element.fieldId}
          position={element.position}
          isOnField={true}
          onRemove={() => removeElementFromField(element.fieldId)}
        />
      ))}
      
      {/* Анимация создания нового элемента */}
      {animation?.visible && (
        <NewElementAnimation
          element={animation.element}
          position={animation.position}
        />
      )}
    </div>
  );
});

// Отображаемое имя компонента для инструментов разработки
GameField.displayName = 'GameField';

export default GameField; 